import { createContext, createElement, use, useCallback, useEffect } from 'react';
import invariant from 'tiny-invariant';
import { z } from 'zod';

import { useEventCallback } from '#utils/use-event-callback.js';
import { CadRendererSyncNamespace } from '../broadcast-manager.js';
import { useSyncSnapshotHydration, useSyncSnapshotResponder } from '../sync-core.js';
import { pickLatest } from '../sync-protocol.js';

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { Utils } from '@cadstrom/sigma-engine-shared';
import type {
	CadRendererViewportChangedEvent as CadRendererViewportChangedEventType,
	ViewportState,
} from '#src/utils/events.js';
import type { CadRendererSyncCore } from '../sync-core.js';
import type { CadRendererActiveView, CadRendererViewSyncValue, ValueRef } from '../sync-types.js';

const CadRendererViewSyncContext = createContext<CadRendererViewSyncValue | null>(null);

const ViewportStateSchema = z.object({
	centerX: z.number(),
	centerY: z.number(),
	scale: z.number(),
}) satisfies Utils.Zot<ViewportState>;

export type ViewSnapshotPayload = {
	requestId?: string;
	designId: string | null;
	instanceId?: string;
	viewport: ViewportState | null;
	updatedAt: number;
	sourceWindowId: string;
};

const ViewSnapshotPayloadSchema = z.object({
	requestId: z.string().optional(),
	designId: z.string().nullable(),
	instanceId: z.string().optional(),
	viewport: ViewportStateSchema.nullable(),
	updatedAt: z.number(),
	sourceWindowId: z.string(),
}) satisfies Utils.Zot<ViewSnapshotPayload>;

export type CadRendererViewSyncProviderProps = {
	value: CadRendererViewSyncValue;
	children: ReactNode;
};

export type CadRendererSyncViewportChangeHandlerProps = {
	revisionId: string;
	onViewportChanged: (viewport: ViewportState) => void;
};

export function CadRendererViewSyncProvider({ value, children }: CadRendererViewSyncProviderProps) {
	return createElement(CadRendererViewSyncContext.Provider, { value }, children);
}

/**
 * Reads view-sync actions used to keep active viewport hydration snapshots fresh.
 */
export function useCadRendererViewSync(): CadRendererViewSyncValue {
	const value = use(CadRendererViewSyncContext);
	invariant(value !== null, 'CadRendererViewSyncContext not provided');
	return value;
}

/**
 * Listens for local viewport changes and marks the view-hydration snapshot timestamp as fresh.
 */
export function CadRendererSyncViewportChangeHandler({
	revisionId,
	onViewportChanged,
}: CadRendererSyncViewportChangeHandlerProps) {
	const { noteViewportChanged } = useCadRendererViewSync();

	const handleViewportChanged = useEventCallback((event: CadRendererViewportChangedEventType) => {
		if (event.detail.revisionId !== revisionId) {
			return;
		}
		noteViewportChanged();
		onViewportChanged(event.detail.viewport);
	});

	useEffect(() => {
		addEventListener('cadRendererViewportChanged', handleViewportChanged);
		return () => removeEventListener('cadRendererViewportChanged', handleViewportChanged);
	}, [handleViewportChanged]);

	return null;
}

/**
 * Bridges the active view (design, optional schematic instance, and viewport) to the sync channel.
 *
 * Participates only in snapshot request/response traffic: it answers peer snapshot requests with the
 * local active view and applies the newest hydration response without recording that restore in local
 * selection history.
 */
export function useViewHydrationBridge({
	core,
	activeViewRef,
	getActiveViewportRef,
	setActiveViewRef,
	restoreViewportRef,
	suppressHistoryForHydrationRef,
	viewUpdatedAtRef,
}: {
	core: CadRendererSyncCore;
	activeViewRef: ValueRef<CadRendererActiveView>;
	getActiveViewportRef: ValueRef<() => ViewportState | null>;
	setActiveViewRef: ValueRef<Dispatch<SetStateAction<CadRendererActiveView>>>;
	restoreViewportRef: ValueRef<(designId: string, instanceId: string | undefined, viewport: ViewportState) => void>;
	suppressHistoryForHydrationRef: ValueRef<() => void>;
	viewUpdatedAtRef: ValueRef<number>;
}) {
	const createSnapshot = useCallback((): ViewSnapshotPayload => {
		const activeView = activeViewRef.current;
		return {
			designId: activeView?.designId ?? null,
			instanceId: activeView?.instanceId,
			viewport: activeView ? getActiveViewportRef.current() : null,
			updatedAt: viewUpdatedAtRef.current,
			sourceWindowId: core.windowId,
		};
	}, [activeViewRef, core.windowId, getActiveViewportRef, viewUpdatedAtRef]);

	const applySnapshot = useCallback(
		(payload: ViewSnapshotPayload) => {
			suppressHistoryForHydrationRef.current();
			if (payload.designId === null) {
				setActiveViewRef.current(null);
				return;
			}

			setActiveViewRef.current({ designId: payload.designId, instanceId: payload.instanceId });
			if (payload.viewport) {
				restoreViewportRef.current(payload.designId, payload.instanceId, payload.viewport);
			}
		},
		[restoreViewportRef, setActiveViewRef, suppressHistoryForHydrationRef],
	);

	useSyncSnapshotResponder({
		core,
		namespace: CadRendererSyncNamespace.ViewHydration,
		createPayload: createSnapshot,
	});

	const applyHydrationResponses = useCallback(
		(responses: readonly ViewSnapshotPayload[]) => {
			const latest = pickLatest(responses);
			if (latest) {
				applySnapshot(latest);
			}
		},
		[applySnapshot],
	);

	useSyncSnapshotHydration({
		core,
		namespace: CadRendererSyncNamespace.ViewHydration,
		parsePayload: parseViewSnapshot,
		applyResponses: applyHydrationResponses,
	});
}

function parseViewSnapshot(value: unknown): ViewSnapshotPayload | null {
	const result = ViewSnapshotPayloadSchema.safeParse(value);
	return result.success ? result.data : null;
}
